import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Users, Heart, Gift, Settings, FileText, LogOut, Plus, Trash2, Check, HandHeart,
  MessageCircle, Mail, Link2, User, Edit3, Languages, HelpCircle, Eye
} from 'lucide-react';
import { EmailTab } from './admin/EmailTab';
import { EmailTemplatesTab } from './admin/EmailTemplatesTab';
import { FAQTab } from './admin/FAQTab';
import { ExportPanel } from './admin/ExportPanel';
import { PeopleTab } from './admin/PeopleTab';
import { ConversationsBulkAI } from './admin/ConversationsBulkAI';
import { SourceBreakdown } from './admin/SourceBreakdown';
import { SitePreviewTab } from './admin/SitePreviewTab';
import { SourcesLinksTab } from './admin/SourcesLinksTab';
import { PrayerUpdatesTab } from './admin/PrayerUpdatesTab';
import { SocialLinksTab } from './admin/SocialLinksTab';
import { TranslationsTab } from './admin/TranslationsTab';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const [dashboard, setDashboard] = useState(null);
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState({});
  const [settingsForm, setSettingsForm] = useState({});
  const [postForm, setPostForm] = useState({ title: '', content: '', title_no: '', content_no: '', visibility: 'public' });
  const [editingPost, setEditingPost] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '' });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');
  const [profileFile, setProfileFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [dashData, postsData, settingsData] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/journey`),
        axios.get(`${API}/admin/settings`, { withCredentials: true })
      ]);
      setDashboard(dashData.data);
      setPosts(postsData.data);
      setSettings(settingsData.data);
      setSettingsForm(settingsData.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const showMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      setUploadedFile(file);
      setUploadedFileUrl(data.url);
      showMessage('Image uploaded');
    } catch (e) { console.error(e); }
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      setProfileFile(file);
      setSettingsForm({ ...settingsForm, profile_image: data.url });
      showMessage('Profile image uploaded');
    } catch (e) { console.error(e); }
  };

  const addPost = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/journey`, {
        title: postForm.title, content: postForm.content,
        title_no: postForm.title_no, content_no: postForm.content_no,
        image_url: uploadedFileUrl || null, visibility: postForm.visibility
      }, { withCredentials: true });
      setPostForm({ title: '', content: '', title_no: '', content_no: '', visibility: 'public' });
      setUploadedFile(null);
      setUploadedFileUrl('');
      showMessage('Post created');
      fetchAllData();
    } finally { setLoading(false); }
  };

  const startEditPost = (post) => {
    setEditingPost(post.id);
    setEditForm({ title: post.title, content: post.content });
  };

  const saveEditPost = async (id) => {
    try {
      await axios.put(`${API}/admin/journey/${id}`, editForm, { withCredentials: true });
      setEditingPost(null);
      showMessage('Post updated');
      fetchAllData();
    } catch (e) { console.error(e); }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await axios.delete(`${API}/admin/journey/${id}`, { withCredentials: true });
    showMessage('Post deleted');
    fetchAllData();
  };

  const updateSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Send the WHOLE form so any field added to settingsForm — new or existing,
      // English or Norwegian — actually saves. Previously this enumerated each
      // field by hand, which meant new fields silently failed to persist.
      const payload = {
        ...settingsForm,
        monthly_goal: parseFloat(settingsForm.monthly_goal),
        one_time_goal: parseFloat(settingsForm.one_time_goal),
        personal_savings: settingsForm.personal_savings ? parseFloat(settingsForm.personal_savings) : 0,
        one_time_offline_adjustment: settingsForm.one_time_offline_adjustment ? parseFloat(settingsForm.one_time_offline_adjustment) : 0,
        use_live_rates: settingsForm.use_live_rates !== false,
        manual_rates: settingsForm.manual_rates || null,
        default_currency: settingsForm.default_currency || 'NOK',
      };
      // Drop the immutable id field so the backend doesn't choke on it.
      delete payload.id;
      delete payload._id;
      await axios.put(`${API}/admin/settings`, payload, { withCredentials: true });
      showMessage('Settings updated');
      fetchAllData();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      if (status === 401 || status === 403) {
        showMessage('Session expired — please log in again. Settings were NOT saved.');
      } else {
        showMessage(`Settings save failed: ${detail}`);
      }
    } finally { setLoading(false); }
  };

  const getTypeIcon = (sup) => {
    const role = sup.supporter_role;
    if (role === 'prayer') return <HandHeart className="w-4 h-4 text-purple-600" />;
    if (role === 'both') return <Heart className="w-4 h-4 text-yellow-600" />;
    if (sup.financial_type === 'one_time') return <Gift className="w-4 h-4 text-blue-600" />;
    return <Heart className="w-4 h-4 text-green-600" />;
  };

  const getRoleLabel = (sup) => {
    if (sup.supporter_role === 'prayer') return 'Prayer';
    if (sup.supporter_role === 'both') return `Both (${sup.financial_type})`;
    return sup.financial_type === 'one_time' ? 'One-time' : 'Monthly';
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      pledged: 'bg-yellow-100 text-yellow-700',
      prayer: 'bg-purple-100 text-purple-700',
      inactive: 'bg-slate-100 text-slate-600'
    };
    return <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pledged}`}>{status}</span>;
  };

  // Tabs ordered communication-first: Overview → People → Comms → Content → Brand/Settings
  const tabs = [
    { id: 'overview', icon: Heart, label: 'Overview' },
    { id: 'site_preview', icon: Eye, label: 'Site Preview' },
    // — People you talk with (unified) —
    { id: 'people', icon: Users, label: 'People' },
    // — Communication —
    { id: 'email', icon: Mail, label: 'Send Email' },
    { id: 'email_templates', icon: Mail, label: 'Email Templates' },
    { id: 'faqs', icon: HelpCircle, label: 'FAQs' },
    { id: 'translations', icon: Languages, label: 'Public Site Text' },
    // — Content —
    { id: 'journey', icon: FileText, label: 'Journey Posts' },
    { id: 'prayer', icon: HandHeart, label: 'Prayer Updates' },
    // — Brand & settings —
    { id: 'social', icon: Link2, label: 'Social Links' },
    { id: 'sources', icon: Link2, label: 'Sources & Wycliffe' },
    { id: 'profile', icon: User, label: 'Profile' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Admin Dashboard</h1>
              <p className="text-sm text-slate-600">Welcome, {user?.name || user?.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="inline" />
              <button data-testid="admin-logout-btn" onClick={handleLogout}
                      className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:text-red-600 font-medium">
                <LogOut className="w-5 h-5" /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {message && (
        <div data-testid="admin-success-message" className="fixed top-20 right-4 bg-green-500 text-white px-6 py-3 rounded-full shadow-lg z-50 animate-slide-in">
          {message}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`admin-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                activeTab === tab.id ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview */}
        {activeTab === 'overview' && dashboard && (
          <div data-testid="admin-overview-tab" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-100 rounded-lg"><Users className="w-5 h-5 text-green-600" /></div>
                  <span className="text-sm font-medium text-slate-600">Active Supporters</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">{dashboard.active_supporters_count}</p>
                <p className="text-sm text-slate-600 mt-1">NOK {dashboard.active_support.toLocaleString()}/month</p>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-yellow-100 rounded-lg"><Heart className="w-5 h-5 text-yellow-600" /></div>
                  <span className="text-sm font-medium text-slate-600">Pledged Supporters</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">{dashboard.pledged_supporters_count}</p>
                <p className="text-sm text-slate-600 mt-1">NOK {dashboard.pledged_support.toLocaleString()}/month</p>
              </div>
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-100 rounded-lg"><Gift className="w-5 h-5 text-blue-600" /></div>
                  <span className="text-sm font-medium text-slate-600">One-Time Gifts</span>
                </div>
                <p className="text-3xl font-bold text-slate-900">NOK {dashboard.one_time_gifts.toLocaleString()}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
              <h3 className="text-xl font-semibold text-slate-800 mb-4">Goals Progress</h3>
              <div className="space-y-6">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">Monthly Goal</span>
                    <span className="text-sm font-bold text-slate-900">
                      {dashboard.monthly_goal > 0 ? Math.round((dashboard.active_support / dashboard.monthly_goal) * 100) : 0}%
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full transition-all duration-500"
                         style={{ width: `${dashboard.monthly_goal > 0 ? Math.min((dashboard.active_support / dashboard.monthly_goal) * 100, 100) : 0}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">One-Time Goal</span>
                    <span className="text-sm font-bold text-slate-900">
                      {dashboard.one_time_goal > 0 ? Math.round((dashboard.one_time_gifts / dashboard.one_time_goal) * 100) : 0}%
                    </span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 rounded-full transition-all duration-500"
                         style={{ width: `${dashboard.one_time_goal > 0 ? Math.min((dashboard.one_time_gifts / dashboard.one_time_goal) * 100, 100) : 0}%` }}></div>
                  </div>
                </div>
              </div>
            </div>
            <SourceBreakdown />
          </div>
        )}

        {activeTab === 'site_preview' && <SitePreviewTab />}

        {/* People (unified — replaces Supporters + External + Conversations) */}
        {activeTab === 'people' && (
          <div data-testid="admin-people-tab-wrap" className="space-y-6">
            {/* AI bulk entry (voice → CRM rows) sits above the table */}
            <ConversationsBulkAI onComplete={fetchAllData} onMessage={showMessage} />
            <ExportPanel onMessage={showMessage} />
            <PeopleTab onMessage={showMessage} />
          </div>
        )}

        {/* Journey Posts */}
        {activeTab === 'journey' && (
          <div data-testid="admin-journey-tab" className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Create Journey Post</h2>
              <form data-testid="add-journey-post-form" onSubmit={addPost} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input data-testid="journey-post-title-input" type="text" placeholder="🇬🇧 Post Title" required
                         value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                         className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
                  <input data-testid="journey-post-title-no-input" type="text" placeholder="🇳🇴 Norwegian title (optional)"
                         value={postForm.title_no} onChange={(e) => setPostForm({ ...postForm, title_no: e.target.value })}
                         className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <textarea data-testid="journey-post-content-input" placeholder="🇬🇧 Post Content" required rows="6"
                            value={postForm.content} onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
                  <textarea data-testid="journey-post-content-no-input" placeholder="🇳🇴 Norwegian content (optional)" rows="6"
                            value={postForm.content_no} onChange={(e) => setPostForm({ ...postForm, content_no: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Visibility</label>
                  <select data-testid="journey-visibility-select"
                          value={postForm.visibility}
                          onChange={(e) => setPostForm({ ...postForm, visibility: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500">
                    <option value="public">Public (everyone)</option>
                    <option value="supporters">Supporters Only</option>
                    <option value="prayer_supporters">Prayer Supporters Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Upload Image (Optional)</label>
                  <input data-testid="journey-post-image-input" type="file" accept="image/*" onChange={handleFileUpload}
                         className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50" />
                  {uploadedFile && <p className="text-sm text-green-600 mt-2">✓ {uploadedFile.name} uploaded</p>}
                </div>
                <button data-testid="add-journey-post-submit-btn" type="submit" disabled={loading}
                        className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50">
                  {loading ? 'Creating...' : 'Create Post'}
                </button>
              </form>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Published Posts</h2>
              {posts.length === 0 ? (
                <p className="text-slate-600">No posts yet.</p>
              ) : (
                <div className="space-y-4">
                  {posts.map(post => (
                    <div key={post.id} className="border border-slate-200 rounded-xl p-6">
                      {editingPost === post.id ? (
                        <div className="space-y-3">
                          <input type="text" value={editForm.title}
                                 onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                 className="w-full px-4 py-2 rounded-xl border-slate-200 bg-slate-50" />
                          <textarea rows="4" value={editForm.content}
                                    onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                                    className="w-full px-4 py-2 rounded-xl border-slate-200 bg-slate-50" />
                          <div className="flex gap-2">
                            <button data-testid={`save-edit-post-btn-${post.id}`} onClick={() => saveEditPost(post.id)}
                                    className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm">Save</button>
                            <button onClick={() => setEditingPost(null)}
                                    className="px-4 py-2 bg-slate-200 text-slate-700 rounded-xl text-sm">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-slate-900 mb-2">{post.title}</h3>
                            <p className="text-slate-600 mb-3 line-clamp-3">{post.content}</p>
                            <p className="text-sm text-slate-500">{new Date(post.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button data-testid={`edit-journey-post-btn-${post.id}`} onClick={() => startEditPost(post)}
                                    className="text-blue-600 hover:text-blue-700">
                              <Edit3 className="w-5 h-5" />
                            </button>
                            <button data-testid={`delete-journey-post-btn-${post.id}`} onClick={() => deletePost(post.id)}
                                    className="text-red-600 hover:text-red-700">
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Email */}
        {activeTab === 'email' && <EmailTab onMessage={showMessage} />}

        {activeTab === 'email_templates' && <EmailTemplatesTab onMessage={showMessage} />}

        {activeTab === 'faqs' && <FAQTab onMessage={showMessage} />}

        {/* Prayer Updates */}
        {activeTab === 'prayer' && <PrayerUpdatesTab onMessage={showMessage} />}

        {/* Social Links */}
        {activeTab === 'social' && <SocialLinksTab onMessage={showMessage} />}

        {/* Sources & Links */}
        {activeTab === 'sources' && <SourcesLinksTab onMessage={showMessage} />}

        {activeTab === 'translations' && <TranslationsTab onMessage={showMessage} />}

        {/* Profile */}
        {activeTab === 'profile' && (
          <div data-testid="admin-profile-tab" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <User className="w-6 h-6 text-blue-600" /> Mission Profile
            </h2>
            <form data-testid="update-profile-form" onSubmit={updateSettings} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">🇬🇧 Mission / Family Name</label>
                  <input data-testid="profile-mission-name-input" type="text" required
                         value={settingsForm.mission_name || ''}
                         onChange={(e) => setSettingsForm({ ...settingsForm, mission_name: e.target.value })}
                         className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">🇳🇴 Norwegian Variant</label>
                  <input data-testid="profile-mission-name-no-input" type="text"
                         value={settingsForm.mission_name_no || ''}
                         onChange={(e) => setSettingsForm({ ...settingsForm, mission_name_no: e.target.value })}
                         className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                         placeholder="Leave blank to use English" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">🇬🇧 Mission Bio / Description</label>
                  <textarea data-testid="profile-bio-input" rows="5"
                            value={settingsForm.bio || ''}
                            onChange={(e) => setSettingsForm({ ...settingsForm, bio: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                            placeholder="Tell visitors about your mission..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">🇳🇴 Norwegian Variant</label>
                  <textarea data-testid="profile-bio-no-input" rows="5"
                            value={settingsForm.bio_no || ''}
                            onChange={(e) => setSettingsForm({ ...settingsForm, bio_no: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                            placeholder="Leave blank to use English" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Profile Image (used as hero background)</label>
                <input data-testid="profile-image-input" type="file" accept="image/*" onChange={handleProfileImageUpload}
                       className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50" />
                {settingsForm.profile_image && (
                  <div className="mt-3">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${settingsForm.profile_image}`}
                         alt="profile preview" className="w-24 h-24 rounded-full object-cover border-2 border-white shadow" />
                  </div>
                )}
              </div>
              <button data-testid="update-profile-submit-btn" type="submit" disabled={loading}
                      className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          </div>
        )}

        {/* Settings */}
        {activeTab === 'settings' && (
          <div data-testid="admin-settings-tab" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Goals, Messages & Currency</h2>
            <form data-testid="update-settings-form" onSubmit={updateSettings} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Monthly Support Goal (NOK)</label>
                  <input data-testid="settings-monthly-goal-input" type="number" required min="1" step="0.01"
                         value={settingsForm.monthly_goal || ''}
                         onChange={(e) => setSettingsForm({ ...settingsForm, monthly_goal: e.target.value })}
                         className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">One-Time Training Goal (NOK)</label>
                  <input data-testid="settings-onetime-goal-input" type="number" required min="1" step="0.01"
                         value={settingsForm.one_time_goal || ''}
                         onChange={(e) => setSettingsForm({ ...settingsForm, one_time_goal: e.target.value })}
                         className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              {/* Manual offline one-time top-up — adds onto the public thermometer */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <label className="block text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                  <Gift className="w-4 h-4" /> Offline one-time gifts (manual top-up)
                </label>
                <p className="text-xs text-amber-900/80 mb-3">
                  Cheques, cash, bank transfers — anything that doesn't flow through the app. This number is added on top of the in-app one-time total and appears on the public thermometer.
                </p>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-slate-700">NOK</span>
                  <input
                    data-testid="settings-offline-onetime-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={settingsForm.one_time_offline_adjustment ?? 0}
                    onChange={(e) => setSettingsForm({ ...settingsForm, one_time_offline_adjustment: parseFloat(e.target.value) || 0 })}
                    className="w-40 px-3 py-2 rounded-lg border-slate-200 bg-white text-sm focus:ring-2 focus:ring-amber-500"
                  />
                  <span className="text-xs text-slate-500">added to one-time gifts shown on the public site</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-1.5">
                    <MessageCircle className="w-4 h-4" /> 🇬🇧 Message to Supporters
                  </label>
                  <textarea data-testid="settings-admin-message-input" rows="4"
                            value={settingsForm.admin_message || ''}
                            onChange={(e) => setSettingsForm({ ...settingsForm, admin_message: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                            placeholder="Shown on supporter dashboard and welcome emails..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">🇳🇴 Norwegian Variant</label>
                  <textarea data-testid="settings-admin-message-no-input" rows="4"
                            value={settingsForm.admin_message_no || ''}
                            onChange={(e) => setSettingsForm({ ...settingsForm, admin_message_no: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                            placeholder="Leave blank to use English" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    🇬🇧 Supporter-Only Teaser Text
                  </label>
                  <textarea data-testid="settings-teaser-text-input" rows="2"
                            value={settingsForm.teaser_text || ''}
                            onChange={(e) => setSettingsForm({ ...settingsForm, teaser_text: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                            placeholder="Supporters receive deeper updates..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">🇳🇴 Norwegian Variant</label>
                  <textarea data-testid="settings-teaser-text-no-input" rows="2"
                            value={settingsForm.teaser_text_no || ''}
                            onChange={(e) => setSettingsForm({ ...settingsForm, teaser_text_no: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                            placeholder="Leave blank to use English" />
                </div>
              </div>

              {/* Currency settings */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Currency Display</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Pledges are always stored in NOK. Visitors can switch their display currency on the home page.
                  Set the default currency shown to visitors who haven't picked one, and choose whether to use live
                  exchange rates or manual overrides.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Default Display Currency</label>
                    <select data-testid="settings-default-currency"
                            value={settingsForm.default_currency || 'NOK'}
                            onChange={(e) => setSettingsForm({ ...settingsForm, default_currency: e.target.value })}
                            className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500">
                      <option value="NOK">NOK – Norwegian Krone</option>
                      <option value="USD">USD – US Dollar</option>
                      <option value="GBP">GBP – British Pound</option>
                      <option value="EUR">EUR – Euro</option>
                    </select>
                  </div>
                  <div className="flex items-center pt-7">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input data-testid="settings-use-live-rates" type="checkbox"
                             checked={settingsForm.use_live_rates !== false}
                             onChange={(e) => setSettingsForm({ ...settingsForm, use_live_rates: e.target.checked })}
                             className="w-4 h-4 text-blue-600 rounded" />
                      <span className="text-sm text-slate-700">Use live exchange rates (refreshed every 12h)</span>
                    </label>
                  </div>
                </div>

                {settingsForm.use_live_rates === false && (
                  <div data-testid="manual-rates-section" className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-xs text-slate-600 mb-3">Manual rates (1 NOK equals…). Used when live rates are disabled.</p>
                    <div className="grid grid-cols-3 gap-3">
                      {['USD', 'GBP', 'EUR'].map(code => (
                        <div key={code}>
                          <label className="block text-xs font-medium text-slate-700 mb-1">{code}</label>
                          <input data-testid={`manual-rate-${code}`} type="number" min="0" step="0.0001"
                                 value={settingsForm.manual_rates?.[code] ?? ''}
                                 onChange={(e) => setSettingsForm({
                                   ...settingsForm,
                                   manual_rates: { ...(settingsForm.manual_rates || {}), [code]: parseFloat(e.target.value) || 0 }
                                 })}
                                 placeholder={code === 'USD' ? '0.094' : code === 'GBP' ? '0.075' : '0.087'}
                                 className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Mission page text */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Mission Page (/mission)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">🇬🇧 Mission Page Intro</label>
                    <textarea data-testid="settings-mission-intro-input" rows="3"
                              value={settingsForm.mission_intro || ''}
                              onChange={(e) => setSettingsForm({ ...settingsForm, mission_intro: e.target.value })}
                              placeholder="Shown at the top of /mission. Describe your calling, why you go, who you serve."
                              className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">🇳🇴 Norwegian Variant</label>
                    <textarea data-testid="settings-mission-intro-no-input" rows="3"
                              value={settingsForm.mission_intro_no || ''}
                              onChange={(e) => setSettingsForm({ ...settingsForm, mission_intro_no: e.target.value })}
                              placeholder="Leave blank to use English"
                              className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
              </div>

              {/* Widgets & Quick Links */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">Widgets & Quick Links</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Edit the Linktree-style links shown on the home page and the newsletter shown on the supporter dashboard.
                </p>

                {/* Hero video + tagline */}
                <div className="p-4 bg-sky-50 rounded-xl mb-4">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">✈️ Home Hero Section</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Background video URL (MP4 direct link, optional)</label>
                      <input data-testid="settings-hero-video-url" type="url"
                             value={settingsForm.hero_video_url || ''}
                             onChange={(e) => setSettingsForm({ ...settingsForm, hero_video_url: e.target.value })}
                             placeholder="https://your-host.com/aviation-loop.mp4 (leave blank to use the hero image)"
                             className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                      <p className="text-xs text-slate-500 mt-1">Tip: short looped video (10–30s, ≤10MB) of sky, plane, or runway works best. Falls back to the profile image.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">🇬🇧 Tagline (under "Welcome Aboard")</label>
                        <input data-testid="settings-hero-tagline" type="text"
                               value={settingsForm.hero_tagline || ''}
                               onChange={(e) => setSettingsForm({ ...settingsForm, hero_tagline: e.target.value })}
                               placeholder="Partnering with Wycliffe and JAARS…"
                               className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">🇳🇴 Norwegian Tagline</label>
                        <input data-testid="settings-hero-tagline-no" type="text"
                               value={settingsForm.hero_tagline_no || ''}
                               onChange={(e) => setSettingsForm({ ...settingsForm, hero_tagline_no: e.target.value })}
                               placeholder="Leave blank to use English"
                               className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mission page hero — YouTube background video */}
                <div className="p-4 bg-rose-50 rounded-xl mb-4">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">🎬 Mission Page Hero Video</h4>
                  <p className="text-xs text-slate-600 mb-3">
                    YouTube video looped/muted behind the Mission page header. Paste a YouTube URL or just the 11-character video ID.
                    <strong className="block mt-1 text-rose-700">
                      ⚠️ The video must have embedding enabled (YouTube Studio → video → Show more → "Allow embedding").
                    </strong>
                  </p>
                  <input
                    data-testid="settings-mission-hero-video"
                    type="text"
                    value={settingsForm.mission_hero_video_url || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, mission_hero_video_url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=U-qvHmDbzyM  or  U-qvHmDbzyM"
                    className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm focus:ring-2 focus:ring-rose-500"
                  />
                </div>

                {/* Journey page hero — image */}
                <div className="p-4 bg-emerald-50 rounded-xl mb-4">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">👨‍👩‍👧 Journey Page Hero Image</h4>
                  <p className="text-xs text-slate-600 mb-3">
                    Image that sits behind the Journey page header. Leave blank to reuse the family photo from your Profile (Home hero).
                  </p>
                  <input
                    data-testid="settings-journey-hero-image"
                    type="text"
                    value={settingsForm.journey_hero_image_url || ''}
                    onChange={(e) => setSettingsForm({ ...settingsForm, journey_hero_image_url: e.target.value })}
                    placeholder="/uploads/your-photo.jpg  or  https://… (leave blank to reuse the profile image)"
                    className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {/* Canva newsletter */}
                <div className="p-4 bg-purple-50 rounded-xl mb-4">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">📄 Canva Newsletter</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">🇬🇧 Canva URL (paste the public /view URL)</label>
                      <input data-testid="settings-canva-url" type="url"
                             value={settingsForm.canva_newsletter_url || ''}
                             onChange={(e) => setSettingsForm({ ...settingsForm, canva_newsletter_url: e.target.value })}
                             placeholder="https://www.canva.com/design/DAxxxx/view"
                             className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">🇳🇴 Norwegian Canva URL (optional)</label>
                      <input data-testid="settings-canva-url-no" type="url"
                             value={settingsForm.canva_newsletter_url_no || ''}
                             onChange={(e) => setSettingsForm({ ...settingsForm, canva_newsletter_url_no: e.target.value })}
                             placeholder="Leave blank to use the EN URL"
                             className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">🇬🇧 Title</label>
                      <input data-testid="settings-canva-title" type="text"
                             value={settingsForm.canva_title || ''}
                             onChange={(e) => setSettingsForm({ ...settingsForm, canva_title: e.target.value })}
                             placeholder="Latest Newsletter"
                             className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">🇳🇴 Norwegian Title</label>
                      <input data-testid="settings-canva-title-no" type="text"
                             value={settingsForm.canva_title_no || ''}
                             onChange={(e) => setSettingsForm({ ...settingsForm, canva_title_no: e.target.value })}
                             placeholder="Siste nyhetsbrev"
                             className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">Thumbnail (shown on home preview card)</label>
                    <input data-testid="settings-canva-thumbnail" type="file" accept="image/*"
                           onChange={async (e) => {
                             const file = e.target.files?.[0];
                             if (!file) return;
                             const fd = new FormData();
                             fd.append('file', file);
                             const { data } = await axios.post(`${API}/files/upload`, fd, {
                               withCredentials: true,
                               headers: { 'Content-Type': 'multipart/form-data' }
                             });
                             setSettingsForm({ ...settingsForm, canva_thumbnail: data.url });
                           }}
                           className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                    {settingsForm.canva_thumbnail && (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${settingsForm.canva_thumbnail}`}
                           alt="thumb" className="mt-2 w-20 h-20 rounded-lg object-cover" />
                    )}
                  </div>
                </div>

                {/* YouTube */}
                <div className="p-4 bg-red-50 rounded-xl mb-4">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5">🎬 YouTube Video (Mission Page)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">🇬🇧 YouTube URL or video ID</label>
                      <input data-testid="settings-youtube-url" type="text"
                             value={settingsForm.youtube_url || ''}
                             onChange={(e) => setSettingsForm({ ...settingsForm, youtube_url: e.target.value })}
                             placeholder="https://www.youtube.com/watch?v=xxxxx"
                             className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">🇳🇴 Norwegian YouTube URL</label>
                      <input data-testid="settings-youtube-url-no" type="text"
                             value={settingsForm.youtube_url_no || ''}
                             onChange={(e) => setSettingsForm({ ...settingsForm, youtube_url_no: e.target.value })}
                             placeholder="Leave blank to use English"
                             className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">🇬🇧 Title</label>
                      <input data-testid="settings-youtube-title" type="text"
                             value={settingsForm.youtube_title || ''}
                             onChange={(e) => setSettingsForm({ ...settingsForm, youtube_title: e.target.value })}
                             placeholder="Watch our story"
                             className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">🇳🇴 Norwegian Title</label>
                      <input data-testid="settings-youtube-title-no" type="text"
                             value={settingsForm.youtube_title_no || ''}
                             onChange={(e) => setSettingsForm({ ...settingsForm, youtube_title_no: e.target.value })}
                             placeholder="Se vår historie"
                             className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                    </div>
                  </div>
                </div>

                {/* About / Wycliffe / JAARS links */}
                <div className="p-4 bg-blue-50 rounded-xl">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">🔗 Info Link Buttons (Mission Page)</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">About Us URL</label>
                      <input data-testid="settings-about-url" type="url"
                             value={settingsForm.about_us_url || ''}
                             onChange={(e) => setSettingsForm({ ...settingsForm, about_us_url: e.target.value })}
                             placeholder="https://your-about-page-link"
                             className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">🇬🇧 Wycliffe URL</label>
                        <input data-testid="settings-wycliffe-info-url" type="url"
                               value={settingsForm.wycliffe_info_url || ''}
                               onChange={(e) => setSettingsForm({ ...settingsForm, wycliffe_info_url: e.target.value })}
                               placeholder="https://wycliffe.org.uk/"
                               className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">🇳🇴 Norwegian Wycliffe URL</label>
                        <input data-testid="settings-wycliffe-info-url-no" type="url"
                               value={settingsForm.wycliffe_info_url_no || ''}
                               onChange={(e) => setSettingsForm({ ...settingsForm, wycliffe_info_url_no: e.target.value })}
                               placeholder="https://www.wycliffe.no/"
                               className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">🇬🇧 JAARS URL</label>
                        <input data-testid="settings-jaars-info-url" type="url"
                               value={settingsForm.jaars_info_url || ''}
                               onChange={(e) => setSettingsForm({ ...settingsForm, jaars_info_url: e.target.value })}
                               placeholder="https://www.jaars.org/about-us"
                               className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">🇳🇴 Norwegian JAARS URL (optional)</label>
                        <input data-testid="settings-jaars-info-url-no" type="url"
                               value={settingsForm.jaars_info_url_no || ''}
                               onChange={(e) => setSettingsForm({ ...settingsForm, jaars_info_url_no: e.target.value })}
                               placeholder="Leave blank to use English"
                               className="w-full px-3 py-2 rounded-lg border-slate-200 bg-white text-sm" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button data-testid="update-settings-submit-btn" type="submit" disabled={loading}
                      className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'Updating...' : 'Save Settings'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
